import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, Image, useWindowDimensions, Platform } from 'react-native';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { SvgIcon } from '../../components/common/SvgIcon';
import { useTranslation, formatNumber } from '../../shared/i18n';
import { scopeChronologicalHorizontal } from '../../shared/i18n/rtl';

const TILE_IMAGE_SIZE = 58;
const TILE_WIDTH = 66;
const TILE_RADIUS = 13;
const TILE_GAP = 10;
const STRIP_MAX_WIDTH = 440;

export interface CalendarMenuStripItem {
  key: string;
  name: string;
  uri: string | null;
  quantity: number;
}

interface CalendarMenuStripProps {
  items: CalendarMenuStripItem[];
  title: string;
  date_label: string;
}

function makeStripStyles(colors: ThemeColors, screen_width: number) {
  const pad_h = appTheme.spacing.lg;
  const strip_max = Math.min(screen_width - pad_h * 2, STRIP_MAX_WIDTH);
  return StyleSheet.create({
    card: {
      marginTop: 0,
      marginBottom: 0,
      marginHorizontal: 0,
      alignSelf: 'center',
      width: '100%',
      maxWidth: strip_max + pad_h * 2,
      borderRadius: 20,
      paddingTop: appTheme.spacing.lg,
      paddingBottom: appTheme.spacing.lg,
      paddingHorizontal: pad_h,
      backgroundColor: colors.surface,
      shadowColor: colors.shadowColor,
      ...appTheme.shadows.xs,
      overflow: 'visible',
    },
    titleBlock: {
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.md,
      gap: 6,
    },
    titleText: {
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1.1,
      textTransform: 'uppercase',
      color: colors.textSecondary,
      textAlign: 'center',
    },
    dateText: {
      fontSize: 22,
      lineHeight: 28,
      fontWeight: '700',
      letterSpacing: -0.35,
      color: colors.primary,
      textAlign: 'center',
    },
    scrollView: {
      minHeight: TILE_IMAGE_SIZE + 34,
    },
    scrollContent: {
      paddingHorizontal: appTheme.spacing.xs,
      paddingVertical: appTheme.spacing.xs,
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: TILE_GAP,
      flexGrow: 1,
    },
    scrollContentEven: {
      justifyContent: 'space-evenly',
      width: '100%',
    },
    emptyWrap: {
      marginHorizontal: 0,
      paddingVertical: appTheme.spacing.xl,
      paddingHorizontal: appTheme.spacing.lg,
      borderRadius: TILE_RADIUS,
      backgroundColor: colors.surfaceElevated,
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.sm,
    },
    emptyText: {
      ...appTheme.typography.bodySmall,
      fontSize: 14,
      lineHeight: 20,
      textAlign: 'center',
      color: colors.textSecondary,
    },
  });
}

function MenuTile({ item, colors }: { item: CalendarMenuStripItem; colors: ThemeColors }) {
  return (
    <View style={tileStyles.tile}>
      <View
        style={[
          tileStyles.imageShell,
          {
            backgroundColor: colors.surfaceElevated,
            shadowColor: colors.shadowColor,
            ...(Platform.OS === 'ios' ? appTheme.shadows.xs : { elevation: 2 }),
          },
        ]}>
        <View style={[tileStyles.imageContainer, { backgroundColor: colors.surfaceElevated }]}>
          {item.uri ? (
            <Image source={{ uri: item.uri }} style={tileStyles.image} resizeMode="cover" />
          ) : (
            <View style={[tileStyles.placeholder, { backgroundColor: hexToRgba(colors.primary, 0.08) ?? colors.primaryUltra }]}>
              <SvgIcon name="meal" size={22} color={colors.primary} />
            </View>
          )}
          {item.quantity > 1 ? (
            <View style={[tileStyles.badge, { backgroundColor: colors.primary }]}>
              <Text style={[tileStyles.badgeText, { color: colors.onPrimary }]}>{formatNumber(item.quantity)}</Text>
            </View>
          ) : null}
        </View>
      </View>
      <Text style={[tileStyles.itemName, { color: colors.text }]} numberOfLines={2} ellipsizeMode="tail">
        {item.name}
      </Text>
    </View>
  );
}

const tileStyles = StyleSheet.create({
  tile: {
    width: TILE_WIDTH,
    alignItems: 'center',
  },
  imageShell: {
    borderRadius: TILE_RADIUS + 2,
    padding: 2,
  },
  imageContainer: {
    width: TILE_IMAGE_SIZE,
    height: TILE_IMAGE_SIZE,
    borderRadius: TILE_RADIUS,
    overflow: 'hidden',
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  placeholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemName: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '600',
    marginTop: 6,
    width: TILE_WIDTH,
    minHeight: 28,
    paddingHorizontal: 2,
    textAlign: 'center',
  },
  badge: {
    position: 'absolute',
    top: 4,
    end: 4,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '700',
  },
});

export function CalendarMenuStrip({ items, title, date_label }: CalendarMenuStripProps) {
  const colors = useThemeColors() ?? appTheme.colors;
  const { t } = useTranslation();
  const { width: screen_width } = useWindowDimensions();
  const styles = useMemo(() => makeStripStyles(colors, screen_width), [colors, screen_width]);
  const strip_inner_width = useMemo(() => {
    const pad_h = appTheme.spacing.lg;
    return Math.min(screen_width - pad_h * 4, STRIP_MAX_WIDTH);
  }, [screen_width]);
  const needs_scroll = useMemo(() => {
    if (items.length === 0) return false;
    const content_pad = appTheme.spacing.xs * 2;
    const content_width = items.length * TILE_WIDTH + Math.max(0, items.length - 1) * TILE_GAP + content_pad;
    return content_width > strip_inner_width;
  }, [items.length, strip_inner_width]);
  const even_row = items.length > 0 && items.length <= 4 && !needs_scroll;

  return (
    <View style={styles.card}>
      <View style={styles.titleBlock}>
        <Text style={styles.titleText}>{title}</Text>
        <Text style={styles.dateText}>{date_label}</Text>
      </View>
      {items.length === 0 ? (
        <View style={styles.emptyWrap}>
          <SvgIcon name="meal" size={28} color={colors.primary} />
          <Text style={styles.emptyText}>{t('calendar.menuStripEmpty')}</Text>
        </View>
      ) : (
        <ScrollView
          style={[styles.scrollView, scopeChronologicalHorizontal]}
          horizontal
          scrollEnabled={needs_scroll}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[styles.scrollContent, even_row && styles.scrollContentEven]}>
          {items.map(item => (
            <MenuTile key={item.key} item={item} colors={colors} />
          ))}
        </ScrollView>
      )}
    </View>
  );
}
